<template>
    <div class="notes">
        <div class="notes__content">
            <TransitionGroup v-if="notes.length" name="list">
                <ItemNote v-for="note in notes" :key="note.id" :note="note" @edit="edit" @delete="deleteNote" />
                <VJoyWrapper v-if="canMoreNotesBeLoaded" justify="center" class="joy-mt-4">
                    <VJoyButton size="xsmall" @click="loadMoreProfileNotes">{{ $t('profile.vetting.drawer.tab.profile.notes.more.cta') }}</VJoyButton>
                </VJoyWrapper>
            </TransitionGroup>
            <EmptyNotes v-else />
        </div>
        <div class="notes__bottom">
            <Transition name="switch">
                <NotesDisclaimer v-if="showDisclaimer && disclaimer" @accept="handleAcceptDisclaimer" />
                <VJoyWrapper v-else no-margin item-stretch justify="center" v-bind="{align: 'center'}">
                    <VJoyTextarea
                        v-model="noteContent"
                        style="height: 54px"
                        :disabled="loadingProfileNotes"
                        :placeholder="$t('profile.vetting.drawer.tab.profile.notes.add.textarea.placeholder')"
                        @focus="handleFocus"
                    />
                    <VJoyButton
                        :disabled="loadingProfileNotes"
                        class="notes__submit-cta"
                        :label="$t('profile.vetting.drawer.tab.profile.notes.add.cta')"
                        @click="add"
                    />
                </VJoyWrapper>
            </Transition>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {useTranslation} from '#imports';
    import {pushVJoySnackbar, VJoyWrapper, VJoyButton, VJoyTextarea} from '@maltjoy/core-vue';
    import {type ProfileNoteResource} from '~/components/admin-section-tabs/notes/types/ProfileNoteResource';
    import {useProfileNotes} from '~/composables/profile-notes/useProfileNotes';
    import type {Profile} from '~/types/domain/Profile';
    import EmptyNotes from './EmptyNotes.vue';
    import ItemNote from './ItemNote.vue';
    import NotesDisclaimer from './NotesDisclaimer.vue';

    const {t} = useTranslation();

    export interface Props {
        profile: Profile;
        more?: boolean;
        disclaimer?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        more: false,
        disclaimer: true,
    });

    const {addProfileNote, editProfileNote, deleteProfileNote, notes, fetchProfileNotes, loadMoreProfileNotes, loadingProfileNotes} = useProfileNotes(
        props.profile,
    );

    await fetchProfileNotes();

    const NUMBER_NOTES_BY_PAGE = 10;
    const noteContent = ref<string>();
    const showDisclaimer = ref<boolean>(false);
    const disclaimerIsRead = ref(false);

    function handleAcceptDisclaimer(): void {
        showDisclaimer.value = false;
        disclaimerIsRead.value = true;
    }

    async function add(): Promise<void> {
        if (noteContent.value) {
            try {
                await addProfileNote(noteContent.value);
                noteContent.value = '';
                displaySuccess(t('profile.vetting.drawer.tab.profile.add.success'));
            } catch (error: any) {
                displayError(error);
            }
        }
    }

    const canMoreNotesBeLoaded = computed((): boolean => notes.value.length % NUMBER_NOTES_BY_PAGE === 0);

    function handleFocus(): void {
        showDisclaimer.value = !notes.value.length && props.disclaimer && !disclaimerIsRead.value;
    }

    async function edit(value: {note: ProfileNoteResource; content: string}) {
        try {
            await editProfileNote(value.note, value.content);
            displaySuccess(t('profile.vetting.drawer.tab.profile.update.success'));
        } catch (error: any) {
            displayError(error);
        }
    }

    async function deleteNote(freelancerNoteId: string) {
        try {
            await deleteProfileNote(freelancerNoteId);
            displaySuccess(t('profile.vetting.drawer.tab.profile.delete.success'));
        } catch (error: any) {
            displayError(error);
        }
    }

    function displayError(error: any) {
        pushVJoySnackbar({
            props: {
                message: error as string,
                level: 'error',
            },
        });
    }

    function displaySuccess(message: string) {
        pushVJoySnackbar({
            props: {
                message,
                level: 'success',
            },
        });
    }
</script>

<style scoped lang="scss">
    .notes {
        position: relative;
        width: 100%;
        height: calc(100vh - 160px);

        &__content {
            height: calc(100% - 70px);
            position: absolute;
            width: 100%;
            overflow-y: auto;
        }

        &__submit-cta {
            max-width: 80px;
        }

        &__bottom {
            bottom: 0;
            width: 100%;
            position: absolute;
            box-shadow: 0px -50px 50px var(--joy-color-neutral-0);
        }
    }

    .list-move,
    .list-enter-active,
    .switch-enter-active,
    .list-leave-active {
        transition: all 800ms;
    }

    .list-enter-from,
    .list-leave-to,
    .switch-enter-from,
    .switch-leave-to {
        opacity: 0;
        transform: translate(0, -20px);
    }

    .list-leave-active,
    .switch-leave-active {
        position: absolute;
    }
</style>
